"use client";

import React from "react";
import { addYears, isAfter, subYears } from "date-fns";
import { OfferResponse, PremiumCorrection } from "@/types/simpego-api";
import { fetchBreeds } from "@/api/simpego";
import { useAgentId } from "@/context/AgentIdContext";
import { readUrlParam } from "@/utils/urlParams";
import { getEarliestStartOfInsurance, createProductVariant, ProductVariant } from "@/utils/funnel";
export type { ProductVariant };
import { isLocalStorageAvailable, loadFunnelFromStorage, saveFunnelToStorage, SerializedFunnelData } from "@/hooks/funnelPersistence";
import { hydrateValuesFromStorage, getDefaultValues } from "@/hooks/funnelSerialization";
import i18n from "@/lib/i18n";
import { ApplicationAnswers, EMPTY_APPLICATION_ANSWERS } from "@/components/contact-details/ApplicationQuestionsSection";
import { BASE_COVERAGES, BaseCoverageId } from "@/config/coverages";
import { CoverageState, EMPTY_COVERAGE_STATE, extractCoverageStateFromOffer } from "@/utils/coveragePayload";

const STORAGE_KEY = "sip-pet-widget-funnelState";

export interface FunnelState {
  contractId: string | null;
  contractToken: string | null;
  offerAgentId: string | null;
  agentId: string | null;
  offerWhitelabelPartner: string | null;
  whitelabelPartner: string | null;
  premiumCorrections: PremiumCorrection[] | null;
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  preexistingConditionCategories: string[];
  preexistingConditionComment: string | null;
  bundlePrices: { S: number; M: number; L: number } | null;
  selectedProduct: ProductVariant | null;
  /** A plan exists once a bundle was chosen — or when the customer jumped in with an offer, which has no bundle. */
  hasSelectedPlan: boolean;
  finalPrice: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  street: string | null;
  housenumber: string | null;
  city: string | null;
  zipCode: string | null;
  country: string | null;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  dailyTermination: boolean;
  bundleRecommendations: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null;
  selectedRecommendation: OfferResponse | null;
  /** The offer behind `finalPrice` (created offer, or the one jumped in with); the confirmation page lists its coverages.
   * Not persisted, like the recommendations. */
  confirmedOffer: OfferResponse | null;
  /** True once a recommendation's fields (per-coverage sumInsured, deductibles, sub-coverages,
   * payment
   * defaults) have been applied to the customer's offer for the first time. Prevents later
   * tier re-selections from silently clobbering edits the customer made on step 3. */
  hasAppliedRecommendation: boolean;
  /** True once the customer has ever landed on the coverage customization page this session —
   * lets "Zurück" from contact details skip straight back to the product variant table when
   * they took the "Auswählen" fast path and never actually saw customization. */
  hasSeenProductCustomization: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  isOfferLoadingFromUrl: boolean;
  isHydrated: boolean;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail: string | null;
  campaignCode: string;
  applicationAnswers: ApplicationAnswers;
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
}

export const useFunnelState = () => {
  const { agentId: contextAgentId, agentToken } = useAgentId();
  const previousContextAgentIdRef = React.useRef<string | null>(contextAgentId);

  const [contractId, setContractId] = React.useState<string | null>(null);
  const [contractToken, setContractToken] = React.useState<string | null>(null);
  const [offerAgentId, setOfferAgentId] = React.useState<string | null>(null);
  const [offerWhitelabelPartner, setOfferWhitelabelPartner] = React.useState<string | null>(null);
  // Like the agent: what is stored in the offer wins, the ?wl= of the URL only fills an empty slot.
  const urlWhitelabelPartner = readUrlParam("wl");
  const [premiumCorrections, setPremiumCorrections] = React.useState<PremiumCorrection[] | null>(null);
  const [petType, setPetType] = React.useState<"cat" | "dog" | null>(null);
  const [petName, setPetName] = React.useState<string | null>(null);
  const [breed, setBreed] = React.useState<string | null>(null);
  const [breedLabel, setBreedLabel] = React.useState<string | null>(null);
  const [birthDate, setBirthDate] = React.useState<Date | undefined>(undefined);
  const [selectedBreedType, setSelectedBreedType] = React.useState<"purebred" | "crossbreed" | "mixed" | null>(null);
  const [gender, setGender] = React.useState<"male" | "female" | "unknown" | null>(null);
  const [isCastrated, setIsCastrated] = React.useState<boolean>(false);
  const [isHouseCat, setIsHouseCat] = React.useState<boolean>(false);
  const [selectedDogSize, setSelectedDogSize] = React.useState<"small" | "large" | null>(null);
  const [chipNumber, setChipNumber] = React.useState<string | null>(null);
  const [preexistingConditionCategories, setPreexistingConditionCategories] = React.useState<string[]>([]);
  const [preexistingConditionComment, setPreexistingConditionComment] = React.useState<string | null>(null);
  // Vorerkrankung chosen only on the comparison page skips the optional questions (they are only
  // asked when it was already selected on step 1). Deliberately not persisted: after a reload the
  // questions are simply asked.
  const [bundlePrices, setBundlePrices] = React.useState<{ S: number; M: number; L: number } | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<ProductVariant | null>(null);
  const [finalPrice, setFinalPrice] = React.useState<number | null>(null);
  const [confirmedOffer, setConfirmedOffer] = React.useState<OfferResponse | null>(null);
  const [contactFirstName, setContactFirstName] = React.useState<string | null>(null);
  const [contactLastName, setContactLastName] = React.useState<string | null>(null);
  const [contactEmail, setContactEmail] = React.useState<string | null>(null);
  const [contactPhone, setContactPhone] = React.useState<string | null>(null);
  const [street, setStreet] = React.useState<string | null>(null);
  const [housenumber, setHousenumber] = React.useState<string | null>(null);
  const [city, setCity] = React.useState<string | null>(null);
  const [zipCode, setZipCode] = React.useState<string | null>(null);
  const [country, setCountry] = React.useState<string | null>(null);
  const [contactBirthdate, setContactBirthdate] = React.useState<Date | undefined>(undefined);
  const [customerGender, setCustomerGender] = React.useState<"male" | "female" | null>(null);
  const [coverageSelections, setCoverageSelections] = React.useState<Partial<Record<BaseCoverageId, boolean>>>(getDefaultValues().coverageSelections);
  const [subCoverageSelections, setSubCoverageSelections] = React.useState<Partial<Record<BaseCoverageId, Record<string, boolean>>>>(EMPTY_COVERAGE_STATE.subCoverageSelections);
  const [subCoverageSumInsured, setSubCoverageSumInsured] = React.useState<Partial<Record<BaseCoverageId, Record<string, number>>>>(EMPTY_COVERAGE_STATE.subCoverageSumInsured);
  const [coverageDeductibles, setCoverageDeductibles] = React.useState<Partial<Record<BaseCoverageId, number>>>(EMPTY_COVERAGE_STATE.coverageDeductibles);
  const [coverageDeductiblesOtherBand, setCoverageDeductiblesOtherBand] = React.useState<Partial<Record<BaseCoverageId, number>>>(EMPTY_COVERAGE_STATE.coverageDeductiblesOtherBand ?? {});
  const [coverageSumInsured, setCoverageSumInsured] = React.useState<Partial<Record<BaseCoverageId, number>>>(EMPTY_COVERAGE_STATE.coverageSumInsured);
  const [coverageSumInsuredPerCase, setCoverageSumInsuredPerCase] = React.useState<Partial<Record<BaseCoverageId, string>>>(EMPTY_COVERAGE_STATE.coverageSumInsuredPerCase);
  const [emergencyCareSumInsuredPerDay, setEmergencyCareSumInsuredPerDay] = React.useState<"50" | "100" | null>(EMPTY_COVERAGE_STATE.emergencyCareSumInsuredPerDay);
  const [annualDeductible, setAnnualDeductible] = React.useState<number | null>(EMPTY_COVERAGE_STATE.annualDeductible ?? null);
  const [dailyTermination, setDailyTermination] = React.useState<boolean>(false);
  const [bundleRecommendations, setBundleRecommendations] = React.useState<{ S: OfferResponse; M: OfferResponse; L: OfferResponse } | null>(null);
  const [selectedRecommendation, setSelectedRecommendation] = React.useState<OfferResponse | null>(null);
  const [hasAppliedRecommendation, setHasAppliedRecommendation] = React.useState<boolean>(false);
  const [hasSeenProductCustomization, setHasSeenProductCustomization] = React.useState<boolean>(false);
  const [paymentInterval, setPaymentInterval] = React.useState<1 | 2 | 4 | 12>(12);
  const [paymentMethod, setPaymentMethod] = React.useState<"credit_card" | "ESR">("credit_card");
  const [startOfInsurance, setStartOfInsuranceState] = React.useState<Date>(getEarliestStartOfInsurance());
  const [firstDue, setFirstDue] = React.useState<Date | null>(null);
  const [isOfferLoadingFromUrl, setIsOfferLoadingFromUrl] = React.useState<boolean>(false);
  const [isHydrated, setIsHydrated] = React.useState<boolean>(false);
  const [dispatchContract, setDispatchContract] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchInvoice, setDispatchInvoice] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchReminder, setDispatchReminder] = React.useState<"customer" | "customer_and_agent" | "agent">("customer");
  const [dispatchEmail, setDispatchEmail] = React.useState<"customer" | "customer_and_agent" | "agent">(
    agentToken ? "agent" : "customer"
  );
  const [agentDispatchEmail, setAgentDispatchEmail] = React.useState<string | null>(null);
  const [campaignCode, setCampaignCode] = React.useState<string>("");
  // Antragsfragen: persisted like the other contact inputs, so they survive stepping forward/back and a reload.
  const [applicationAnswers, setApplicationAnswers] = React.useState<ApplicationAnswers>(EMPTY_APPLICATION_ANSWERS);
  const [offerLoadedFromUrl, setOfferLoadedFromUrl] = React.useState<boolean>(false);
  const [hasLeftConfirmationAfterOfferLoad, setHasLeftConfirmationAfterOfferLoad] = React.useState<boolean>(false);
  const [consultationProtocolDocumentId, setConsultationProtocolDocumentId] = React.useState<number | null>(null);
  const [consultationProtocolFilename, setConsultationProtocolFilename] = React.useState<string | null>(null);
  const [consultationProtocolPendingFile, setConsultationProtocolPendingFile] = React.useState<string | null>(null);
  const [consultationProtocolPendingFilename, setConsultationProtocolPendingFilename] = React.useState<string | null>(null);

  const hydrateFromStorage = React.useCallback(() => {
    const data = loadFunnelFromStorage(STORAGE_KEY);
    if (!data) {
      setDispatchEmail(agentToken ? "agent" : "customer");
      setIsHydrated(true);
      return;
    }
    const hydrated = hydrateValuesFromStorage(data, agentToken ? "agent" : "customer");

    setContractId(hydrated.contractId);
    setContractToken(hydrated.contractToken);
    setOfferAgentId(hydrated.offerAgentId ?? null);
    setOfferWhitelabelPartner(hydrated.offerWhitelabelPartner ?? null);
    setPetType(hydrated.petType);
    setPetName(hydrated.petName);
    setBreed(hydrated.breed);
    setBreedLabel(hydrated.breedLabel);
    setBirthDate(hydrated.birthDate);
    setSelectedBreedType(hydrated.selectedBreedType);
    setGender(hydrated.gender);
    setIsCastrated(hydrated.isCastrated);
    setIsHouseCat(hydrated.isHouseCat);
    setSelectedDogSize(hydrated.selectedDogSize);
    setChipNumber(hydrated.chipNumber);
    setPreexistingConditionCategories(hydrated.preexistingConditionCategories);
    setPreexistingConditionComment(hydrated.preexistingConditionComment);
    setBundlePrices(hydrated.bundlePrices);
    setSelectedProduct(hydrated.selectedProduct);
    setFinalPrice(hydrated.finalPrice);
    setContactFirstName(hydrated.contactFirstName);
    setContactLastName(hydrated.contactLastName);
    setContactEmail(hydrated.contactEmail);
    setContactPhone(hydrated.contactPhone);
    setStreet(hydrated.street);
    setHousenumber(hydrated.housenumber);
    setCity(hydrated.city);
    setZipCode(hydrated.zipCode);
    setCountry(hydrated.country);
    setContactBirthdate(hydrated.contactBirthdate);
    setCustomerGender(hydrated.customerGender);
    setCoverageSelections(hydrated.coverageSelections);
    setSubCoverageSelections(hydrated.subCoverageSelections);
    setSubCoverageSumInsured(hydrated.subCoverageSumInsured);
    setCoverageDeductibles(hydrated.coverageDeductibles);
    setCoverageDeductiblesOtherBand(hydrated.coverageDeductiblesOtherBand);
    setCoverageSumInsured(hydrated.coverageSumInsured);
    setCoverageSumInsuredPerCase(hydrated.coverageSumInsuredPerCase);
    setEmergencyCareSumInsuredPerDay(hydrated.emergencyCareSumInsuredPerDay);
    setAnnualDeductible(hydrated.annualDeductible ?? null);
    setDailyTermination(hydrated.dailyTermination);
    setHasAppliedRecommendation(hydrated.hasAppliedRecommendation);
    setHasSeenProductCustomization(hydrated.hasSeenProductCustomization);
    setPaymentInterval(hydrated.paymentInterval);
    setPaymentMethod(hydrated.paymentMethod);
    setStartOfInsuranceState(hydrated.startOfInsurance);
    setFirstDue(hydrated.firstDue);
    setIsOfferLoadingFromUrl(hydrated.isOfferLoadingFromUrl);
    setDispatchContract(hydrated.dispatchContract);
    setDispatchInvoice(hydrated.dispatchInvoice);
    setDispatchReminder(hydrated.dispatchReminder);
    setDispatchEmail(hydrated.dispatchEmail);
    setAgentDispatchEmail(hydrated.agentDispatchEmail);
    setCampaignCode(hydrated.campaignCode);
    setApplicationAnswers(hydrated.applicationAnswers);
    setPremiumCorrections(hydrated.premiumCorrections);
    setOfferLoadedFromUrl(hydrated.offerLoadedFromUrl);
    setHasLeftConfirmationAfterOfferLoad(hydrated.hasLeftConfirmationAfterOfferLoad);
    setConsultationProtocolDocumentId(hydrated.consultationProtocolDocumentId);
    setConsultationProtocolFilename(hydrated.consultationProtocolFilename);
    setConsultationProtocolPendingFile(hydrated.consultationProtocolPendingFile);
    setConsultationProtocolPendingFilename(hydrated.consultationProtocolPendingFilename);

    setIsHydrated(true);
  }, [
    agentToken, setPreexistingConditionCategories, setPreexistingConditionComment,
    setContractId, setContractToken, setOfferAgentId, setOfferWhitelabelPartner, setPetType, setPetName, setBreed, setBreedLabel, setBirthDate,
    setSelectedBreedType, setGender, setIsCastrated, setIsHouseCat, setSelectedDogSize, setBundlePrices,
    setSelectedProduct, setFinalPrice, setConfirmedOffer, setContactFirstName, setContactLastName, setContactEmail,
    setContactPhone, setStreet, setHousenumber, setCity, setZipCode, setCountry, setContactBirthdate,
    setCustomerGender, setCoverageSelections, setSubCoverageSelections, setSubCoverageSumInsured, setCoverageDeductibles, setCoverageDeductiblesOtherBand,
    setCoverageSumInsured, setCoverageSumInsuredPerCase, setEmergencyCareSumInsuredPerDay, setAnnualDeductible, setDailyTermination, setHasAppliedRecommendation,
    setHasSeenProductCustomization,
    setPaymentInterval, setPaymentMethod, setStartOfInsuranceState, setFirstDue,
    setIsOfferLoadingFromUrl, setDispatchContract, setDispatchInvoice, setDispatchReminder, setDispatchEmail,
    setAgentDispatchEmail, setCampaignCode, setApplicationAnswers, setPremiumCorrections, setOfferLoadedFromUrl, setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocumentId, setConsultationProtocolFilename,
    setConsultationProtocolPendingFile, setConsultationProtocolPendingFilename,
    setChipNumber
  ]);

  React.useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  React.useEffect(() => {
    if (!selectedProduct?.id) return;

    const syncSelectedProductLabels = () => {
      setSelectedProduct(createProductVariant(selectedProduct.id as "s" | "m" | "l"));
    };

    // Ensure hydrated products use the current locale immediately
    syncSelectedProductLabels();

    i18n.on("languageChanged", syncSelectedProductLabels);
    return () => {
      i18n.off("languageChanged", syncSelectedProductLabels);
    };
  }, [selectedProduct?.id]);

  React.useEffect(() => {
    const previousAgentId = previousContextAgentIdRef.current;
    if (!contextAgentId && previousAgentId) {
      setDispatchContract("customer");
      setDispatchInvoice("customer");
      setDispatchReminder("customer");
      setDispatchEmail("customer");
      setAgentDispatchEmail(null);
    }
    previousContextAgentIdRef.current = contextAgentId;
  }, [contextAgentId]);

  React.useEffect(() => {
    if (!offerLoadedFromUrl || !petType || !breed || breedLabel || selectedBreedType === "mixed") return;

    let isActive = true;
    const hydrateBreedLabel = async () => {
      try {
        const breeds = await fetchBreeds(petType, i18n.language);
        const match = breeds.find((item) => item.code === breed);
        if (isActive && match?.translation) {
          setBreedLabel(match.translation);
        }
      } catch (error) {
        console.error("Failed to fetch breed translations for offer:", error);
      }
    };

    hydrateBreedLabel();
    return () => {
      isActive = false;
    };
  }, [offerLoadedFromUrl, petType, breed, breedLabel, selectedBreedType, i18n.language]);

  const handlePetSelection = (selectedPetType: "cat" | "dog") => {
    setPetType(selectedPetType);

    // Clear dependent data to prevent premature premium calculations
    setPetName(null);
    setBreed(null);
    setBreedLabel(null);
    setBirthDate(undefined);
    setSelectedBreedType(null);
    setGender(null);
    setIsCastrated(false);
    setIsHouseCat(false);
    setSelectedDogSize(null);
    setChipNumber(null);
    // The Vorerkrankung categories are kept (like on untick) so an earlier answer is prefilled, not asked again.

    // Reset pricing-related state so queries stay disabled
    setBundlePrices(null);
    setBundleRecommendations(null);
    setSelectedProduct(null);
    setSelectedRecommendation(null);
    setHasAppliedRecommendation(false);
    setHasSeenProductCustomization(false);
    setFinalPrice(null);
    setConfirmedOffer(null);
  };

  const handlePetDetailsSubmit = (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedCoverageSelections: Partial<Record<BaseCoverageId, boolean>>,
    submittedStartOfInsurance: Date,
    submittedChipNumber: string | null
  ) => {
    setPetName(submittedPetName);
    setBreed(selectedBreed);
    setBreedLabel(submittedBreedLabel ?? null);
    setBirthDate(submittedBirthDate);
    setSelectedBreedType(submittedSelectedBreedType);
    setGender(submittedGender);
    setIsCastrated(submittedIsCastrated);
    setIsHouseCat(submittedIsHouseCat);
    setSelectedDogSize(submittedSelectedDogSize);
    setCoverageSelections(submittedCoverageSelections);
    setStartOfInsurance(submittedStartOfInsurance);
    setChipNumber(submittedChipNumber);
  };

  /** Reseeds only the coverage-related step-3 state (deductibles/sub-toggles/VS) from a
   * recommendation's figures. Deliberately leaves payment interval/method, daily termination,
   * start of insurance, and every other funnel field untouched — those aren't "coverage". */
  const reseedCoverageStateFromRecommendation = (recommendation: OfferResponse) => {
    const coverageState = extractCoverageStateFromOffer(recommendation);
    // The live backend doesn't always echo back a deductiblePercentage for every coverage on
    // the /recommendations response — e.g. it reliably returns one for `disease` but often
    // omits `accident`'s, even though both share the same Selbstbehalt concept in this
    // product. Rather than leaving those blank (which blocks checkout until the customer
    // notices and fills them in manually), backfill any selected deductible-bearing coverage
    // that's missing a figure with `disease`'s, sourced from this same response.
    const coverageDeductiblesWithFallback = { ...coverageState.coverageDeductibles };
    const coverageDeductiblesOtherBandWithFallback = { ...coverageState.coverageDeductiblesOtherBand };
    const diseasePct = coverageDeductiblesWithFallback.disease;
    const diseaseOtherBandPct = coverageDeductiblesOtherBandWithFallback.disease;
    if (diseasePct !== undefined) {
      for (const def of BASE_COVERAGES) {
        if (def.deductible === "none") continue;
        if (!coverageState.coverageSelections[def.id]) continue;
        if (coverageDeductiblesWithFallback[def.id] !== undefined) continue;
        coverageDeductiblesWithFallback[def.id] = diseasePct;
        if (def.deductible === "ageBanded" && diseaseOtherBandPct !== undefined) {
          coverageDeductiblesOtherBandWithFallback[def.id] = diseaseOtherBandPct;
        }
      }
    }
    setSubCoverageSelections(coverageState.subCoverageSelections);
    setSubCoverageSumInsured(coverageState.subCoverageSumInsured);
    setCoverageDeductibles(coverageDeductiblesWithFallback);
    setCoverageDeductiblesOtherBand(coverageDeductiblesOtherBandWithFallback);
    setCoverageSumInsured(coverageState.coverageSumInsured);
    setCoverageSumInsuredPerCase(coverageState.coverageSumInsuredPerCase);
    setEmergencyCareSumInsuredPerDay(coverageState.emergencyCareSumInsuredPerDay);
    setAnnualDeductible(coverageState.annualDeductible ?? null);
  };

  /** Applies a chosen bundle tier's live recommendation: sets it as selected, and — only the
   * first time a recommendation is ever selected — seeds the step-3 editable coverage state
   * and its payment defaults from its returned figures. Later re-selections (e.g. switching
   * tiers after already customizing on step 3) leave the customer's edits alone; only the
   * selected product/price update. */
  const applyRecommendation = (product: ProductVariant, recommendation: OfferResponse) => {
    // A tier switch (e.g. picking L after S) must always reseed coverage figures from that
    // tier's own recommendation — S's sum insured/deductibles are meaningless once the
    // product is L, and leaving them in place produces a mismatched live price and summary.
    // Only the very first-ever application also resets payment defaults; switching tiers
    // afterward leaves payment interval/method/daily termination as the customer set them.
    const isFirstApplication = !hasAppliedRecommendation;
    const isDifferentProduct = selectedProduct?.id !== product.id;

    setSelectedProduct(product);
    setSelectedRecommendation(recommendation);
    setFinalPrice(recommendation.grossPremiumProRata ?? null);

    if (isFirstApplication || isDifferentProduct) {
      reseedCoverageStateFromRecommendation(recommendation);
      setHasAppliedRecommendation(true);
    }
    if (isFirstApplication) {
      setPaymentInterval(12);
      setPaymentMethod("credit_card");
      setDailyTermination(!!recommendation.dailyTermination?.selected);
    }
  };

  /** Discards only the customer's step-3 *coverage* edits (deductibles/sub-toggles/VS) by
   * reseeding them from the currently selected tier's recommendation — used when the customer
   * navigates back to step 1 or 2. Payment interval/method, daily termination, start of
   * insurance, and everything else the customer already entered is left untouched. */
  const discardCoverageCustomizations = (product: ProductVariant | null, recommendation: OfferResponse | null) => {
    if (!product || !recommendation) return;
    setSelectedRecommendation(recommendation);
    setFinalPrice(recommendation.grossPremiumProRata ?? null);
    reseedCoverageStateFromRecommendation(recommendation);
    setHasAppliedRecommendation(true);
  };

  /** "Anpassungen verwerfen": drops the chosen tier and everything customized on top of it, so the
   * customer picks a package afresh. The next selection counts as the first one again (coverage
   * figures and payment defaults are reseeded from it); what was entered on step 1 stays. */
  const discardProductSelection = () => {
    setSelectedProduct(null);
    setSelectedRecommendation(null);
    setFinalPrice(null);
    setConfirmedOffer(null);
    setHasAppliedRecommendation(false);
    setHasSeenProductCustomization(false);
  };

  /** Coverage checkboxes on the comparison page: the table must reflect the new selection, so the
   * chosen tier is dropped and the recommendations are cleared (FunnelContainer refetches them). */
  const changeCoverageSelectionsOnComparison = (next: Partial<Record<BaseCoverageId, boolean>>) => {
    setCoverageSelections(next);
    discardProductSelection();
    setBundleRecommendations(null);
  };

  const handlePriceCustomizationSubmit = (
    product: ProductVariant | null,
    price: number,
    submittedCoverageDeductibles: Partial<Record<BaseCoverageId, number>>,
    submittedSubCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>,
    submittedCoverageSumInsured: Partial<Record<BaseCoverageId, number>>,
    submittedDailyTermination: boolean,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => {
    setSelectedProduct(product);
    setFinalPrice(price);
    setConfirmedOffer(null);
    setCoverageDeductibles(submittedCoverageDeductibles);
    setSubCoverageSelections(submittedSubCoverageSelections);
    setCoverageSumInsured(submittedCoverageSumInsured);
    setDailyTermination(submittedDailyTermination);
    setPaymentInterval(interval);
    setPaymentMethod(method);
    setStartOfInsurance(startDate);
  };

  const handleContactDetailsSubmit = (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    city: string,
    zip: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => {
    setContactFirstName(firstName);
    setContactLastName(lastName);
    setContactEmail(email);
    setContactPhone(phone);
    setStreet(submittedStreet);
    setHousenumber(submittedHousenumber);
    setCity(city);
    setZipCode(zip);
    setCountry(country);
    setContactBirthdate(birthdate);
    setCustomerGender(gender === "" ? null : gender);
  };

  const setFunnelStateFromOffer = (offer: OfferResponse): string => {
    const incomingContractId = offer.contractID || null;
    const incomingContractToken = offer.contractToken || null;
    if (incomingContractId !== contractId || incomingContractToken !== contractToken) {
      setConsultationProtocolDocumentId(null);
      setConsultationProtocolFilename(null);
      setConsultationProtocolPendingFile(null);
      setConsultationProtocolPendingFilename(null);
    }
    setContractId(incomingContractId);
    setContractToken(incomingContractToken);
    // What is stored in the offer wins over the agent of the URL/token, like its whitelabel partner.
    setOfferAgentId(offer.agentID ?? null);
    // The bundle of an offer isn't meaningful (the backend always returns "L" for Pet 2.0),
    // so jumping in with an offer leaves the bundle unselected.
    setSelectedProduct(null);
    setOfferLoadedFromUrl(true);
    setHasLeftConfirmationAfterOfferLoad(false);
    setOfferWhitelabelPartner(offer.whitelabelPartner ?? null);
    // The discount the offer came in with (Einsprung) has to survive every later request.
    setPremiumCorrections(offer.product?.productControl?.premiumCorrections ?? null);

    if (offer.product && offer.product.pets && offer.product.pets.length > 0) {
      const pet = offer.product.pets[0];
      setPetType(pet.species.toLowerCase() as "cat" | "dog");
      setPetName(pet.name || null);
      setBreed(pet.breed || null);
      setBreedLabel(null);
      setBirthDate(pet.birthdate ? new Date(pet.birthdate) : undefined);
      setGender(pet.gender || null);
      setIsCastrated(pet.castration || false);
      setIsHouseCat(pet.housecat || false);
      setChipNumber(pet.chipNumber || null);
      const offerCategories = pet.coverages?.preexistingConditions?.categories;
      // The backend can serialize an empty list as `{}`; the API schema only accepts an array.
      setPreexistingConditionCategories(Array.isArray(offerCategories) ? offerCategories : []);
      setPreexistingConditionComment(pet.coverages?.preexistingConditions?.categoryComment ?? null);

      if (
        pet.breed === "mischlingskatze" ||
        pet.breed === "mischling_klein" ||
        (typeof pet.breed === "string" && pet.breed.includes("mischling_gross"))
      ) {
        setSelectedBreedType("mixed");
        if (pet.species === "Dog") {
          setSelectedDogSize(pet.breed === "mischling_klein" ? "small" : "large");
        }
      } else {
        setSelectedBreedType(pet.crossbreed ? "crossbreed" : "purebred");
      }

      const coverageState = extractCoverageStateFromOffer(offer);
      setCoverageSelections(coverageState.coverageSelections);
      setSubCoverageSelections(coverageState.subCoverageSelections);
      setSubCoverageSumInsured(coverageState.subCoverageSumInsured);
      setCoverageDeductibles(coverageState.coverageDeductibles);
      setCoverageDeductiblesOtherBand(coverageState.coverageDeductiblesOtherBand ?? {});
      setCoverageSumInsured(coverageState.coverageSumInsured);
      setCoverageSumInsuredPerCase(coverageState.coverageSumInsuredPerCase);
      setEmergencyCareSumInsuredPerDay(coverageState.emergencyCareSumInsuredPerDay);
      setAnnualDeductible(coverageState.annualDeductible ?? null);
      setSelectedRecommendation(offer);
      setHasAppliedRecommendation(true);
    }
    setDailyTermination(!!offer.dailyTermination?.selected);

    if (offer.customer && offer.customer.objectType === "NaturalPerson") {
      setContactFirstName(offer.customer.firstname || null);
      setContactLastName(offer.customer.name || null);
      setContactEmail(offer.customer.email || null);
      const phoneDetail = offer.customer.contactDetails?.find(cd => cd.category === "cellphone" || cd.category === "phone");
      setContactPhone(phoneDetail?.value || null);
      setStreet(offer.customer.address?.street || null);
      setHousenumber(offer.customer.address?.housenumber || null);
      setCity(offer.customer.address?.city || null);
      setZipCode(offer.customer.address?.zip || null);
      setCountry(offer.customer.address?.countryCode || null);

      setContactBirthdate(offer.customer.birthdate ? new Date(offer.customer.birthdate) : undefined);
      const offeredGender = typeof offer.customer.gender === "string" ? offer.customer.gender.toLowerCase() : null;
    setCustomerGender(offeredGender === "male" || offeredGender === "female" ? offeredGender : null);
    }

    // Set dispatch control settings
    const asDispatchTarget = (value: string | undefined): "customer" | "agent" | "customer_and_agent" =>
      value === "agent" || value === "customer_and_agent" ? value : "customer";
    setDispatchContract(asDispatchTarget(offer.dispatchControl?.contract));
    setDispatchInvoice(asDispatchTarget(offer.dispatchControl?.invoice));
    setDispatchReminder(asDispatchTarget(offer.dispatchControl?.reminder));
    const offerEmailSetting =
      (offer.dispatchControl as any)?.orderEmails ||
      (offer.dispatchControl as any)?.orderEmail ||
      (offer.dispatchControl as any)?.offerEmails ||
      (offer.dispatchControl as any)?.offerEmail ||
      (agentToken ? "agent" : "customer");
    setDispatchEmail(offerEmailSetting);
    setAgentDispatchEmail(offer.dispatchControl?.agentEmail || null);
    setCampaignCode(offer.campaignCode || "");
    // The backend does not return the Antragsfragen, but an offer can only exist once both were answered eligibly
    // ("no" to termination/rejection, "yes" to owner/keeper), so a re-entry must not ask for them again.
    setApplicationAnswers({ termination: "no", owner: "yes" });

    setPaymentInterval(offer.yearlyPaymentFrequency?.frequency || 12);
    setPaymentMethod(offer.paymentMethod || "credit_card");
    // Jumping in with an existing offer link must still respect the earliest-start floor
    // (tomorrow, but never before EARLIEST_START_OF_INSURANCE) — the offer's own
    // startOfInsurance may be today, in the past or before the product launch by the time the
    // link is opened, so only trust it when it's still at or after the floor.
    const earliestStart = getEarliestStartOfInsurance();
    const offerStartOfInsurance = offer.startOfInsurance ? new Date(offer.startOfInsurance) : null;
    setStartOfInsuranceState(
      offerStartOfInsurance && !Number.isNaN(offerStartOfInsurance.getTime()) && offerStartOfInsurance >= earliestStart
        ? offerStartOfInsurance
        : earliestStart
    );
    if (offer.firstDue) {
      const parsedFirstDue = new Date(offer.firstDue);
      setFirstDue(Number.isNaN(parsedFirstDue.getTime()) ? null : parsedFirstDue);
    } else {
      setFirstDue(null);
    }

    setFinalPrice(offer.grossPremiumProRata || null);
    setConfirmedOffer(offer);

    const consultationDocument = offer.documents?.find((doc) => doc.kind === "consultation_protocol");
    if (consultationDocument && typeof consultationDocument.documentId === "number") {
      setConsultationProtocolDocumentId(consultationDocument.documentId);
      setConsultationProtocolFilename(consultationDocument.filename || null);
    } else {
      setConsultationProtocolDocumentId(null);
      setConsultationProtocolFilename(null);
    }
    if (consultationDocument?.file && consultationDocument.filename) {
      setConsultationProtocolPendingFile(consultationDocument.file);
      setConsultationProtocolPendingFilename(consultationDocument.filename);
    } else {
      setConsultationProtocolPendingFile(null);
      setConsultationProtocolPendingFilename(null);
    }
    return "routes.confirmation";
  };

  const handleResetFunnel = React.useCallback(() => {
    const defaults = getDefaultValues({ defaultDispatchEmail: agentToken ? "agent" : "customer" });

    setContractId(defaults.contractId);
    setContractToken(defaults.contractToken);
    setOfferAgentId(defaults.offerAgentId);
    setPetType(defaults.petType);
    setPetName(defaults.petName);
    setBreed(defaults.breed);
    setBreedLabel(defaults.breedLabel);
    setBirthDate(defaults.birthDate);
    setSelectedBreedType(defaults.selectedBreedType);
    setGender(defaults.gender);
    setIsCastrated(defaults.isCastrated);
    setIsHouseCat(defaults.isHouseCat);
    setSelectedDogSize(defaults.selectedDogSize);
    setChipNumber(defaults.chipNumber);
    setPreexistingConditionCategories(defaults.preexistingConditionCategories);
    setPreexistingConditionComment(defaults.preexistingConditionComment);
    setBundlePrices(defaults.bundlePrices);
    setSelectedProduct(defaults.selectedProduct);
    setFinalPrice(defaults.finalPrice);
    setConfirmedOffer(null);
    setContactFirstName(defaults.contactFirstName);
    setContactLastName(defaults.contactLastName);
    setContactEmail(defaults.contactEmail);
    setContactPhone(defaults.contactPhone);
    setStreet(defaults.street);
    setHousenumber(defaults.housenumber);
    setCity(defaults.city);
    setZipCode(defaults.zipCode);
    setCountry(defaults.country);
    setContactBirthdate(defaults.contactBirthdate);
    setCustomerGender(defaults.customerGender);
    setCoverageSelections(defaults.coverageSelections);
    setSubCoverageSelections(defaults.subCoverageSelections);
    setSubCoverageSumInsured(defaults.subCoverageSumInsured);
    setCoverageDeductibles(defaults.coverageDeductibles);
    setCoverageDeductiblesOtherBand(defaults.coverageDeductiblesOtherBand ?? {});
    setCoverageSumInsured(defaults.coverageSumInsured);
    setCoverageSumInsuredPerCase(defaults.coverageSumInsuredPerCase);
    setEmergencyCareSumInsuredPerDay(defaults.emergencyCareSumInsuredPerDay);
    setAnnualDeductible(defaults.annualDeductible ?? null);
    setDailyTermination(defaults.dailyTermination);
    setBundleRecommendations(null);
    setSelectedRecommendation(null);
    setHasAppliedRecommendation(false);
    setHasSeenProductCustomization(false);
    setPaymentInterval(defaults.paymentInterval);
    setPaymentMethod(defaults.paymentMethod);
    setStartOfInsuranceState(defaults.startOfInsurance);
    setFirstDue(defaults.firstDue);
    setIsOfferLoadingFromUrl(defaults.isOfferLoadingFromUrl);
    setDispatchContract(defaults.dispatchContract);
    setDispatchInvoice(defaults.dispatchInvoice);
    setDispatchReminder(defaults.dispatchReminder);
    setDispatchEmail(defaults.dispatchEmail);
    setAgentDispatchEmail(defaults.agentDispatchEmail);
    setCampaignCode(defaults.campaignCode);
    setApplicationAnswers(EMPTY_APPLICATION_ANSWERS);
    setOfferWhitelabelPartner(defaults.offerWhitelabelPartner);
    setPremiumCorrections(defaults.premiumCorrections);
    setOfferLoadedFromUrl(defaults.offerLoadedFromUrl);
    setHasLeftConfirmationAfterOfferLoad(defaults.hasLeftConfirmationAfterOfferLoad);
    setConsultationProtocolDocumentId(defaults.consultationProtocolDocumentId);
    setConsultationProtocolFilename(defaults.consultationProtocolFilename);
    setConsultationProtocolPendingFile(defaults.consultationProtocolPendingFile);
    setConsultationProtocolPendingFilename(defaults.consultationProtocolPendingFilename);

    if (isLocalStorageAvailable()) {
      localStorage.removeItem(STORAGE_KEY);
    }

    setIsHydrated(true);
  }, [
    agentToken, setPreexistingConditionCategories, setPreexistingConditionComment,
    setContractId, setContractToken, setOfferAgentId, setPetType, setPetName, setBreed, setBreedLabel, setBirthDate,
    setSelectedBreedType, setGender, setIsCastrated, setIsHouseCat, setSelectedDogSize, setBundlePrices,
    setSelectedProduct, setFinalPrice, setConfirmedOffer, setContactFirstName, setContactLastName, setContactEmail,
    setContactPhone, setStreet, setHousenumber, setCity, setZipCode, setCountry, setContactBirthdate,
    setCustomerGender, setCoverageSelections, setSubCoverageSelections, setSubCoverageSumInsured, setCoverageDeductibles, setCoverageDeductiblesOtherBand,
    setCoverageSumInsured, setCoverageSumInsuredPerCase, setEmergencyCareSumInsuredPerDay, setAnnualDeductible, setDailyTermination, setBundleRecommendations, setSelectedRecommendation, setHasAppliedRecommendation,
    setHasSeenProductCustomization,
    setPaymentInterval, setPaymentMethod, setStartOfInsuranceState,
    setIsOfferLoadingFromUrl, setDispatchContract, setDispatchInvoice, setDispatchReminder, setDispatchEmail,
    setAgentDispatchEmail, setCampaignCode, setApplicationAnswers, setOfferWhitelabelPartner, setPremiumCorrections, setOfferLoadedFromUrl, setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocumentId, setConsultationProtocolFilename,
    setConsultationProtocolPendingFile, setConsultationProtocolPendingFilename,
    setChipNumber
  ]);

  const setConsultationProtocolDocument = React.useCallback((documentId: number, filename: string) => {
    setConsultationProtocolDocumentId(documentId);
    setConsultationProtocolFilename(filename);
  }, []);

  const clearConsultationProtocolDocument = React.useCallback(() => {
    setConsultationProtocolDocumentId(null);
    setConsultationProtocolFilename(null);
  }, []);

  const setConsultationProtocolPending = React.useCallback((base64File: string, filename: string) => {
    setConsultationProtocolPendingFile(base64File);
    setConsultationProtocolPendingFilename(filename);
  }, []);

  const clearConsultationProtocolPending = React.useCallback(() => {
    setConsultationProtocolPendingFile(null);
    setConsultationProtocolPendingFilename(null);
  }, []);

  const funnelState: FunnelState = React.useMemo(() => {
    const effectiveAgentId = offerAgentId ?? contextAgentId;
    return {
      contractId,
      contractToken,
      offerAgentId,
      agentId: effectiveAgentId,
      offerWhitelabelPartner,
      whitelabelPartner: offerWhitelabelPartner ?? urlWhitelabelPartner,
      premiumCorrections,
      petType,
      petName,
      breed,
      breedLabel,
      birthDate,
      selectedBreedType,
      gender,
      isCastrated,
      isHouseCat,
      selectedDogSize,
      chipNumber,
      preexistingConditionCategories,
      preexistingConditionComment,
      bundlePrices,
      selectedProduct,
      finalPrice,
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhone,
      street,
      housenumber,
      city,
      zipCode,
      country,
      contactBirthdate,
      customerGender,
      coverageSelections,
      subCoverageSelections,
      subCoverageSumInsured,
      coverageDeductibles,
      coverageDeductiblesOtherBand,
      coverageSumInsured,
      coverageSumInsuredPerCase,
      emergencyCareSumInsuredPerDay,
      annualDeductible,
      dailyTermination,
      bundleRecommendations,
      selectedRecommendation,
      confirmedOffer,
      hasAppliedRecommendation,
      hasSelectedPlan: !!selectedProduct || hasAppliedRecommendation,
      hasSeenProductCustomization,
      paymentInterval,
      paymentMethod,
      startOfInsurance,
      firstDue,
      isOfferLoadingFromUrl,
      isHydrated,
      dispatchContract,
      dispatchInvoice,
      dispatchReminder,
      dispatchEmail,
      agentDispatchEmail,
      campaignCode,
      applicationAnswers,
      offerLoadedFromUrl,
      hasLeftConfirmationAfterOfferLoad,
      consultationProtocolDocumentId,
      consultationProtocolFilename,
      consultationProtocolPendingFile,
      consultationProtocolPendingFilename,
    };
  }, [
    contractId,
    contractToken,
    contextAgentId,
    offerAgentId,
    offerWhitelabelPartner,
    urlWhitelabelPartner,
    premiumCorrections,
    petType,
    petName,
    breed,
    breedLabel,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    preexistingConditionCategories,
    preexistingConditionComment,
    bundlePrices,
    selectedProduct,
    finalPrice,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    contactBirthdate,
    customerGender,
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageDeductiblesOtherBand,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
    dailyTermination,
    bundleRecommendations,
    selectedRecommendation,
    confirmedOffer,
    hasAppliedRecommendation,
    hasSeenProductCustomization,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    isOfferLoadingFromUrl,
    isHydrated,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    campaignCode,
    applicationAnswers,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId,
    consultationProtocolFilename,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
  ]);

  const adjustFirstDueForStart = React.useCallback((currentFirstDue: Date | null, nextStart: Date) => {
    if (!currentFirstDue || Number.isNaN(currentFirstDue.getTime())) return currentFirstDue;
    if (Number.isNaN(nextStart.getTime())) return currentFirstDue;

    if (isAfter(nextStart, currentFirstDue)) {
      return addYears(currentFirstDue, 1);
    }

    const oneYearAfterStart = addYears(nextStart, 1);
    if (isAfter(currentFirstDue, oneYearAfterStart)) {
      let adjustedFirstDue = currentFirstDue;
      while (isAfter(adjustedFirstDue, oneYearAfterStart)) {
        adjustedFirstDue = subYears(adjustedFirstDue, 1);
      }
      return adjustedFirstDue;
    }

    return currentFirstDue;
  }, []);

  const setStartOfInsurance = React.useCallback((nextStart: Date) => {
    setStartOfInsuranceState(nextStart);
    setFirstDue((currentFirstDue) => adjustFirstDueForStart(currentFirstDue, nextStart));
  }, [adjustFirstDueForStart]);

  React.useEffect(() => {
    if (!isHydrated) return;
    const data: SerializedFunnelData = {
      contractId: funnelState.contractId,
      contractToken: funnelState.contractToken,
      offerAgentId: funnelState.offerAgentId,
      offerWhitelabelPartner: funnelState.offerWhitelabelPartner,
      petType: funnelState.petType,
      petName: funnelState.petName,
      breed: funnelState.breed,
      breedLabel: funnelState.isHydrated ? funnelState.breedLabel : null, // Only serialize if hydrated
      birthDate: funnelState.birthDate ? funnelState.birthDate.toISOString() : null,
      selectedBreedType: funnelState.selectedBreedType,
      gender: funnelState.gender,
      isCastrated: funnelState.isCastrated,
      isHouseCat: funnelState.isHouseCat,
      selectedDogSize: funnelState.selectedDogSize,
      chipNumber: funnelState.chipNumber,
      preexistingConditionCategories: funnelState.preexistingConditionCategories,
      preexistingConditionComment: funnelState.preexistingConditionComment,
      bundlePrices: funnelState.bundlePrices,
      selectedProductId: funnelState.selectedProduct?.id ?? null,
      finalPrice: funnelState.finalPrice,
      contactFirstName: funnelState.contactFirstName,
      contactLastName: funnelState.contactLastName,
      contactEmail: funnelState.contactEmail,
      contactPhone: funnelState.contactPhone,
      street: funnelState.street,
      housenumber: funnelState.housenumber,
      city: funnelState.city,
      zipCode: funnelState.zipCode,
      country: funnelState.country,
      contactBirthdate: funnelState.contactBirthdate ? funnelState.contactBirthdate.toISOString() : null,
      customerGender: funnelState.customerGender,
      coverageSelections: funnelState.coverageSelections,
      subCoverageSelections: funnelState.subCoverageSelections,
      subCoverageSumInsured: funnelState.subCoverageSumInsured,
      coverageDeductibles: funnelState.coverageDeductibles,
      coverageDeductiblesOtherBand: funnelState.coverageDeductiblesOtherBand,
      coverageSumInsured: funnelState.coverageSumInsured,
      coverageSumInsuredPerCase: funnelState.coverageSumInsuredPerCase,
      emergencyCareSumInsuredPerDay: funnelState.emergencyCareSumInsuredPerDay,
      annualDeductible: funnelState.annualDeductible,
      dailyTermination: funnelState.dailyTermination,
      hasAppliedRecommendation: funnelState.hasAppliedRecommendation,
      hasSeenProductCustomization: funnelState.hasSeenProductCustomization,
      paymentInterval: funnelState.paymentInterval,
      paymentMethod: funnelState.paymentMethod,
      startOfInsurance: funnelState.startOfInsurance ? funnelState.startOfInsurance.toISOString() : null,
      firstDue:
        funnelState.firstDue && !Number.isNaN(funnelState.firstDue.getTime())
          ? funnelState.firstDue.toISOString()
          : null,
      isOfferLoadingFromUrl: funnelState.isOfferLoadingFromUrl,
      dispatchContract: funnelState.dispatchContract,
      dispatchInvoice: funnelState.dispatchInvoice,
      dispatchReminder: funnelState.dispatchReminder,
      dispatchEmail: funnelState.dispatchEmail,
      agentDispatchEmail: funnelState.agentDispatchEmail,
      campaignCode: funnelState.campaignCode,
      applicationAnswers: funnelState.applicationAnswers,
      premiumCorrections: funnelState.premiumCorrections,
      offerLoadedFromUrl: funnelState.offerLoadedFromUrl,
      hasLeftConfirmationAfterOfferLoad: funnelState.hasLeftConfirmationAfterOfferLoad,
      consultationProtocolDocumentId: funnelState.consultationProtocolDocumentId,
      consultationProtocolFilename: funnelState.consultationProtocolFilename,
      // The not yet uploaded protocol (Base64, up to ~6.7 MB) stays in memory only: it would blow the localStorage quota.
    };
    saveFunnelToStorage(STORAGE_KEY, data);
  }, [isHydrated, funnelState]);

  return {
    funnelState,
    setBundlePrices,
    setBundleRecommendations,
    setSelectedRecommendation,
    setConfirmedOffer,
    setHasAppliedRecommendation,
    setChipNumber,
    setCoverageSelections,
    setSubCoverageSelections,
    setSubCoverageSumInsured,
    setCoverageDeductibles,
    setCoverageDeductiblesOtherBand,
    setCoverageSumInsured,
    setCoverageSumInsuredPerCase,
    setEmergencyCareSumInsuredPerDay,
    setAnnualDeductible,
    setDailyTermination,
    setPaymentInterval,
    setPaymentMethod,
    setStartOfInsurance,
    setFirstDue,
    setSelectedProduct,
    setFinalPrice,
    setContractId,
    setContractToken,
    setIsOfferLoadingFromUrl,
    setDispatchContract,
    setDispatchInvoice,
    setDispatchReminder,
    setDispatchEmail,
    setAgentDispatchEmail,
    setCampaignCode,
    applicationAnswers,
    setApplicationAnswers,
    setPremiumCorrections,
    setOfferLoadedFromUrl,
    setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocument,
    clearConsultationProtocolDocument,
    setConsultationProtocolPending,
    clearConsultationProtocolPending,
    handlePetSelection,
    handlePetDetailsSubmit,
    applyRecommendation,
    discardCoverageCustomizations,
    discardProductSelection,
    changeCoverageSelectionsOnComparison,
    setPreexistingConditionCategories,
    setPreexistingConditionComment,
    setHasSeenProductCustomization,
    handlePriceCustomizationSubmit,
    handleContactDetailsSubmit,
    setFunnelStateFromOffer,
    handleResetFunnel,
  };
};
